import { MapPin, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { errorMessage } from '../../lib/api';
import { geocodeAddress } from '../../lib/geoApi';
import { Alert } from '../Alert';
import { Button } from '../Button';
import { Dialog } from '../Dialog';
import { PinPickerMap } from './index';

interface PinDialogProps {
  open: boolean;
  address: string;
  initial: { lat: number; lng: number } | null;
  onSave: (p: { lat: number; lng: number }) => void;
  onClose: () => void;
}

type Lookup = { state: 'idle' } | { state: 'searching' } | { state: 'found'; label: string } | { state: 'missing'; message: string };

/** Places or corrects the pin of an address; starts from the geocoded position when there is none. */
export function PinDialog({ open, address, initial, onSave, onClose }: PinDialogProps) {
  const [point, setPoint] = useState(initial);
  const [lookup, setLookup] = useState<Lookup>({ state: 'idle' });

  async function find() {
    setLookup({ state: 'searching' });
    try {
      const res = await geocodeAddress(address);
      if (res.place) {
        setPoint({ lat: res.place.lat, lng: res.place.lng });
        setLookup({
          state: 'found',
          label:
            res.place.precision === 'house'
              ? `Znaleziono: ${res.place.address}. Sprawdź, czy pinezka stoi na właściwym budynku.`
              : `Znaleziono tylko ${res.place.precision === 'street' ? 'ulicę' : 'miejscowość'}: ${res.place.address}. Przesuń pinezkę na budynek.`,
        });
      } else {
        setLookup({
          state: 'missing',
          message: res.available
            ? 'Nie znaleziono tego adresu. Dotknij mapy w miejscu budynku.'
            : 'Wyszukiwanie adresów chwilowo nie działa. Dotknij mapy w miejscu budynku.',
        });
      }
    } catch (err) {
      setLookup({ state: 'missing', message: errorMessage(err) });
    }
  }

  useEffect(() => {
    if (open && !initial && address.trim().length >= 3) void find();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog
      open={open}
      title="Położenie na mapie"
      onClose={onClose}
      actions={
        <>
          <Button variant="primary" size="lg" icon={<MapPin aria-hidden="true" />} disabled={!point} onClick={() => point && onSave(point)}>
            Zapisz położenie
          </Button>
          <Button variant="secondary" size="lg" onClick={onClose}>
            Anuluj
          </Button>
        </>
      }
    >
      {address && <p><strong>{address}</strong></p>}
      {lookup.state === 'found' && <Alert kind="info">{lookup.label}</Alert>}
      {lookup.state === 'missing' && <Alert kind="warning">{lookup.message}</Alert>}
      <PinPickerMap value={point} onChange={setPoint} height="min(55dvh, 26rem)" />
      <p>Przeciągnij pinezkę albo dotknij mapy w miejscu budynku.</p>
      {address.trim().length >= 3 && (
        <div>
          <Button variant="ghost" icon={<Search aria-hidden="true" />} onClick={find} disabled={lookup.state === 'searching'}>
            {lookup.state === 'searching' ? 'Szukam…' : 'Znajdź adres na mapie'}
          </Button>
        </div>
      )}
    </Dialog>
  );
}
